/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: utils/formDefaults.ts (Queues)
 🗓️ Created: 2025-01-15
 🧩 Description:
    Default form state values for Queues module.

────────────────────────────────────────────────────────────────*/

import { FormState, Strategy, TimeBaseScore, AgentType, AgentStateMode, AgentFormState } from '../types';

/**
 * Default form state
 */
export function getDefaultFormState(): FormState {
  return {
    // Tab 1: General
    queueName: '',
    queueSlug: '',
    extension: '',
    strategy: 'longest-idle-agent',
    recordCalls: false,
    maxWaitTime: '0',
    maxWaitTimeWithNoAgent: '300',
    maxWaitTimeWithNoAgentTimeReached: '',
    enabled: true,
    queueProfileId: '',
    callbackEnabled: false,
    callbackProfileId: '',
    vipListIds: [],
    wrapUpTime: '10',
    queuePriority: '0',
    afterAgentHangupDestinationModule: '',
    afterAgentHangupDestinationDestination: '',
    timeoutDestinationModule: '',
    timeoutDestinationDestination: '',
    serviceLevelSeconds: '0',
    announceHoldTime: false,
    announcePosition: false,
    
    // Tab 2: Announcements
    greeting: '',
    welcomeSound: '',
    exitSound: '',
    language: 'en-us',
    musicOnHold: 'local_stream://default',
    agentConnectSound: '',
    announceSound: '',
    announceFrequency: '0',
    announceEstimatedHoldTime: false,
    
    // Tab 3: Advanced
    timeBaseScore: 'queue',
    timeBaseScoreSeconds: '0',
    discardAbandonedAfter: '60',
    abandonedResumeAllowed: false,
    callerExitKey: '',
    maxWaitAgents: '0',
    maxNoAnswer: '3',
    queueMaxSize: '0',
    agentNoAnswerDelay: '0',
    
    // Tab 4: Caller Experience
    callerIdNamePrefix: '',
    callerIdNumberPrefix: '',
    
    // Tab 5: Tier Rules
    tierRulesApply: false,
    tierRuleWait: '0',
    tierRuleMultiplyLevel: false,
    tierRuleNoAgentNoWait: false,
    
    createdAt: null,
    updatedAt: null
  };
}

/**
 * Default agent form state
 */
export function getDefaultAgentFormState(): AgentFormState {
  return {
    extension: '',
    contactTimeout: '30',
    agentNoAnswerDelayTime: '0',
    rejectDelayTime: '0',
    busyDelayTime: '0',
    maxNoAnswer: '3',
    agentType: 'Inbound',
    stateMode: 'auto'
  };
}

/**
 * Strategy options
 */
export const STRATEGY_OPTIONS: Strategy[] = [
  'ring-all',
  'longest-idle-agent',
  'round-robin',
  'top-down',
  'least-talk-time',
  'least-calls',
  'sequentially-by-agent-order',
  'sequentially-by-next-agent-order',
  'random'
];

/**
 * Time base score options
 */
export const TIME_BASE_SCORE_OPTIONS: TimeBaseScore[] = ['queue', 'system'];

/**
 * Agent type options
 */
export const AGENT_TYPE_OPTIONS: AgentType[] = ['Inbound', 'Outbound', 'Callback'];

/**
 * Agent state mode options
 */
export const AGENT_STATE_MODE_OPTIONS: AgentStateMode[] = ['auto', 'manual'];

